import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'

const NodeConfigModal = ({ node, isOpen, onClose, onSave }) => {
  const [config, setConfig] = useState({})
  const [templates, setTemplates] = useState([])
  const [activeCampaigns, setActiveCampaigns] = useState([])

  // Load email templates and active campaigns from localStorage
  useEffect(() => {
    const savedTemplates = JSON.parse(localStorage.getItem('email_templates') || '[]')
    setTemplates(savedTemplates)
    
    // Load active campaigns
    const savedCampaigns = JSON.parse(localStorage.getItem('campaigns') || '[]')
    const active = savedCampaigns.filter(c => 
      ['Active', 'Scheduled', 'In Progress'].includes(c.status)
    )
    setActiveCampaigns(active)
  }, [])

  useEffect(() => {
    if (node && node.data) {
      setConfig(node.data.config || {})
    }
  }, [node])

  if (!isOpen || !node) return null

  const handleSave = () => {
    onSave(node.id, config)
    toast.success('Node configuration saved')
    onClose()
  }

  const renderConfigFields = () => {
    const nodeType = node.type

    switch (nodeType) {
      case 'triggerNode':
        return renderTriggerConfig()
      case 'actionNode':
        return renderActionConfig()
      case 'conditionNode':
        return renderConditionConfig()
      case 'delayNode':
        return renderDelayConfig()
      case 'dataNode':
        return renderDataConfig()
      default:
        return <p className="text-gray-600">No configuration needed</p>
    }
  }

  const renderTriggerConfig = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Trigger Type</label>
          <select
            value={config.triggerType || ''}
            onChange={(e) => setConfig({ ...config, triggerType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value="">Select trigger...</option>
            <option value="lead_added">New Lead Added</option>
            <option value="email_opened">Email Opened</option>
            <option value="email_clicked">Link Clicked</option>
            <option value="email_replied">Email Reply Received</option>
            <option value="form_submitted">Form Submitted</option>
            <option value="tag_added">Tag Added to Contact</option>
            <option value="schedule">Time-Based Schedule</option>
          </select>
        </div>

        {config.triggerType === 'schedule' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Schedule</label>
            <select
              value={config.schedule || ''}
              onChange={(e) => setConfig({ ...config, schedule: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select schedule...</option>
              <option value="daily">Daily at 9:00 AM</option>
              <option value="weekly">Weekly on Monday</option>
              <option value="monthly">Monthly on 1st</option>
            </select>
          </div>
        )}

        {config.triggerType === 'tag_added' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tag Name</label>
            <input
              type="text"
              value={config.tagName || ''}
              onChange={(e) => setConfig({ ...config, tagName: e.target.value })}
              placeholder="e.g., hot-lead"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
            />
          </div>
        )}
      </div>
    )
  }

  const renderActionConfig = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
          <select
            value={config.actionType || ''}
            onChange={(e) => setConfig({ ...config, actionType: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select action...</option>
            <option value="send_email">Send Email</option>
            <option value="send_sms">Send SMS</option>
            <option value="add_to_list">Add to Contact List</option>
            <option value="validate_email">Validate Email</option>
            <option value="create_deal">Create CRM Deal</option>
            <option value="book_appointment">Book Appointment</option>
            <option value="send_notification">Send Notification</option>
            <option value="webhook">Call Webhook</option>
          </select>
        </div>

        {config.actionType === 'send_email' && (
          <>
            {/* Email Sending Mode Selection */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-4 mb-4">
              <label className="block text-sm font-bold text-gray-800 mb-3">📧 Email Sending Mode</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, emailMode: 'template' })}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    config.emailMode !== 'campaign' 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-lg' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}
                >
                  <div className="text-lg mb-1">📝</div>
                  <div className="font-semibold text-sm">Single Email</div>
                  <div className="text-xs opacity-80">Use a template</div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setConfig({ ...config, emailMode: 'campaign' })}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    config.emailMode === 'campaign' 
                      ? 'bg-purple-600 text-white border-purple-600 shadow-lg' 
                      : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                  }`}
                >
                  <div className="text-lg mb-1">🚀</div>
                  <div className="font-semibold text-sm">Active Campaign</div>
                  <div className="text-xs opacity-80">Auto-enroll leads</div>
                </button>
              </div>
            </div>

            {/* Campaign Selection Mode */}
            {config.emailMode === 'campaign' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🎯 Select Active Campaign
                  </label>
                  <select
                    value={config.campaignId || ''}
                    onChange={(e) => {
                      const campaignId = e.target.value
                      const campaign = activeCampaigns.find(c => c.id === campaignId)
                      setConfig({ 
                        ...config, 
                        campaignId: campaignId,
                        campaignName: campaign?.name || '',
                        campaignSubject: campaign?.subject || '',
                        campaignTargetAudience: campaign?.targetAudience || '',
                        campaignBatchSize: campaign?.batchSize || 25,
                        campaignSendDate: campaign?.sendDate || ''
                      })
                    }}
                    className="w-full px-3 py-2 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  >
                    <option value="">Select a campaign...</option>
                    {activeCampaigns.map(campaign => (
                      <option key={campaign.id} value={campaign.id}>
                        {campaign.name} - {campaign.status} ({campaign.targetAudience})
                      </option>
                    ))}
                  </select>
                  {activeCampaigns.length === 0 && (
                    <p className="text-xs text-red-600 mt-1 font-medium">
                      ⚠️ No active campaigns found. Create a campaign first in Outreach Automation.
                    </p>
                  )}
                </div>
                
                {config.campaignId && (
                  <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-lg p-4">
                    <div className="flex items-start gap-2 mb-3">
                      <span className="text-2xl">📊</span>
                      <div>
                        <p className="text-sm font-bold text-purple-900">Campaign Details</p>
                        <p className="text-xs text-gray-600">Leads will be automatically enrolled</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">📧 Name:</span>
                        <span className="text-gray-900">{config.campaignName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">📝 Subject:</span>
                        <span className="text-gray-900">{config.campaignSubject}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">🎯 Target:</span>
                        <span className="text-gray-900">{config.campaignTargetAudience}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">📦 Batch Size:</span>
                        <span className="text-gray-900">{config.campaignBatchSize} emails/batch</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-700">📅 Schedule:</span>
                        <span className="text-gray-900">{config.campaignSendDate || 'Not set'}</span>
                      </div>
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <p className="text-xs text-purple-800 font-medium">
                        ✨ <strong>Auto-Enrollment:</strong> When this workflow runs, new leads will be automatically added to this campaign and receive emails according to the campaign's schedule and batch settings.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Template Selection Mode (Original) */}
            {config.emailMode !== 'campaign' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Template</label>
                  <select
                    value={config.emailTemplateId || ''}
                    onChange={(e) => {
                      const templateId = e.target.value
                      const template = templates.find(t => t.id === templateId)
                      setConfig({ 
                        ...config, 
                        emailTemplateId: templateId,
                        emailTemplateName: template?.name || '',
                        emailSubject: template?.subject || '',
                        emailBody: template?.body || ''
                      })
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select template...</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </option>
                    ))}
                  </select>
                  {templates.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      No templates found. Go to Outreach Automation → Email Templates to create one.
                    </p>
                  )}
                </div>
                
                {config.emailTemplateId && (
                  <>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-700 mb-1">Selected Template:</p>
                      <p className="text-sm font-semibold text-gray-900">{config.emailTemplateName}</p>
                      <p className="text-xs text-gray-600 mt-2">
                        <strong>Subject:</strong> {config.emailSubject}
                      </p>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        <strong>Body Preview:</strong> {config.emailBody?.substring(0, 100)}...
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {config.actionType === 'add_to_list' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact List</label>
            <input
              type="text"
              value={config.listName || ''}
              onChange={(e) => setConfig({ ...config, listName: e.target.value })}
              placeholder="List name..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {config.actionType === 'webhook' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
            <input
              type="url"
              value={config.webhookUrl || ''}
              onChange={(e) => setConfig({ ...config, webhookUrl: e.target.value })}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>
    )
  }

  const renderConditionConfig = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Condition Field</label>
          <select
            value={config.conditionField || ''}
            onChange={(e) => setConfig({ ...config, conditionField: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="">Select field...</option>
            <option value="email_opened">Email Opened</option>
            <option value="email_clicked">Email Clicked</option>
            <option value="tag">Has Tag</option>
            <option value="lead_score">Lead Score</option>
            <option value="custom_field">Custom Field</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Operator</label>
          <select
            value={config.operator || ''}
            onChange={(e) => setConfig({ ...config, operator: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="">Select operator...</option>
            <option value="equals">Equals</option>
            <option value="not_equals">Not Equals</option>
            <option value="contains">Contains</option>
            <option value="greater_than">Greater Than</option>
            <option value="less_than">Less Than</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Value</label>
          <input
            type="text"
            value={config.conditionValue || ''}
            onChange={(e) => setConfig({ ...config, conditionValue: e.target.value })}
            placeholder="Condition value..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
          />
        </div>
      </div>
    )
  }

  const renderDelayConfig = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Wait Duration</label>
          <div className="flex gap-2">
            <input
              type="number"
              value={config.delayAmount || ''}
              onChange={(e) => setConfig({ ...config, delayAmount: e.target.value })}
              placeholder="5"
              min="1"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            />
            <select
              value={config.delayUnit || 'minutes'}
              onChange={(e) => setConfig({ ...config, delayUnit: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
            >
              <option value="minutes">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
            </select>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-sm text-orange-800">
            <strong>Preview:</strong> Wait {config.delayAmount || '0'} {config.delayUnit || 'minutes'} before continuing to next step
          </p>
        </div>
      </div>
    )
  }

  const renderDataConfig = () => {
    return (
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Data Operation</label>
          <select
            value={config.dataOperation || ''}
            onChange={(e) => setConfig({ ...config, dataOperation: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="">Select operation...</option>
            <option value="add_tag">Add Tag</option>
            <option value="remove_tag">Remove Tag</option>
            <option value="update_field">Update Field</option>
            <option value="update_score">Update Lead Score</option>
            <option value="save_to_db">Save to Database</option>
          </select>
        </div>

        {(config.dataOperation === 'add_tag' || config.dataOperation === 'remove_tag') && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tag Name</label>
            <input
              type="text"
              value={config.tagValue || ''}
              onChange={(e) => setConfig({ ...config, tagValue: e.target.value })}
              placeholder="e.g., interested, qualified"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        )}

        {config.dataOperation === 'update_field' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Field Name</label>
              <input
                type="text"
                value={config.fieldName || ''}
                onChange={(e) => setConfig({ ...config, fieldName: e.target.value })}
                placeholder="e.g., status, industry"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Field Value</label>
              <input
                type="text"
                value={config.fieldValue || ''}
                onChange={(e) => setConfig({ ...config, fieldValue: e.target.value })}
                placeholder="New value..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </>
        )}

        {config.dataOperation === 'update_score' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Score Change</label>
            <input
              type="number"
              value={config.scoreChange || ''}
              onChange={(e) => setConfig({ ...config, scoreChange: e.target.value })}
              placeholder="+10 or -5"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white">
          <h2 className="text-xl font-bold">Configure Node</h2>
          <p className="text-sm opacity-90">{node.data?.label || 'Node Settings'}</p>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
          {renderConfigFields()}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition"
          >
            Save Configuration
          </button>
        </div>
      </div>
    </div>
  )
}

export default NodeConfigModal
